// src/components/VirtualPiano.js
// Virtual Piano with dynamic range support + iPad-safe sample playback.
//
// ✅ iPad/Safari fix:
// - NO fetch(HEAD/Range) probing (can fail on iPad over localhost/LAN dev server)
// - Resolve sample URLs by creating an Audio() and waiting for "loadedmetadata"/"canplaythrough"
// - Cache resolved URL per note (string) or null (not found)
// - Reuse Audio elements per URL (no new Audio() per tap)
// - Keep AudioContext only as fallback synth (should almost never be used now)
//
// ✅ NEW: Electric-piano style synth fallback (smooth + low-pass + slight chorus + gentle tremolo)
// - Used ONLY on iPad/tablets when samples fail
// - Darker/softer than sine/square
// - Cuts harsh highs, sounds soothing

import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";

// Tries both casings to be robust across dev/prod
const AUDIO_BASES = ["/audio", "/Audio"];
const AUDIO_EXTS = ["wav", "mp3", "ogg"];

// Export the normalizer so RealPianoListener & LevelTemplate can use the same logic.
export const normalizeNoteName = (n) =>
  String(n || "")
    .replace(/[0-9]/g, "") // strip octave digits
    .replace(/([A-G])b/g, (_, L) => {
      // flats → sharps
      const map = { A: "G#", B: "A#", D: "C#", E: "D#", G: "F#" };
      return map[L] || `${L}b`;
    })
    .trim();

/* ─────────────────────────────────────────────
   DEVICE DETECTION (iPad/tablets only)
   ───────────────────────────────────────────── */

// iPadOS 13+ sometimes reports as "MacIntel" with touch points.
function isProbablyIPad() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/i.test(ua);
  const iPadLike =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadLike;
}

// Broad "tablet-ish" detection (Android tablets + iPad).
function isProbablyTablet() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  const isMobile = /Mobile/i.test(ua);
  const androidTablet = isAndroid && !isMobile; // many tablets omit "Mobile"
  const iPad = isProbablyIPad();
  return iPad || androidTablet;
}

/* ─────────────────────────────────────────────
   NOTE/MIDI HELPERS
   ───────────────────────────────────────────── */

const PC_INDEX = {
  C: 0,
  "C#": 1,
  D: 2,
  "D#": 3,
  E: 4,
  F: 5,
  "F#": 6,
  G: 7,
  "G#": 8,
  A: 9,
  "A#": 10,
  B: 11,
};

const INDEX_TO_PC = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];

// We treat octave-less notes as octave 4 (your existing convention)
function parseNote(noteRaw) {
  const s = String(noteRaw || "").trim();
  const m = s.match(/^([A-Ga-g])(#)?(\d+)?$/);
  if (!m) return null;

  const L = m[1].toUpperCase();
  const sharp = !!m[2];
  const oct = m[3] != null ? parseInt(m[3], 10) : 4;

  const pc = sharp ? `${L}#` : L;
  if (!(pc in PC_INDEX)) return null;

  return { letter: L, sharp, pc, octave: oct };
}

function noteToMidi(noteRaw) {
  const p = parseNote(noteRaw);
  if (!p) return null;
  // MIDI: C-1 = 0 => (octave + 1) * 12 + pcIndex
  return (p.octave + 1) * 12 + PC_INDEX[p.pc];
}

function midiToOctPc(midi) {
  const pcIndex = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  return { pc: INDEX_TO_PC[pcIndex], octave };
}

// Preserve your display/attribute naming style
function canonicalWhiteName(letter, octave) {
  // Middle octave (C4–B4) uses no octave label in your UI
  if (octave === 4) return letter;
  return `${letter}${octave}`;
}

function canonicalBlackName(letter, octave) {
  // Middle octave black keys also omit octave (C#, D#, F#, G#, A#)
  if (octave === 4) return `${letter}#`;
  return `${letter}#${octave}`;
}

function isNaturalPc(pc) {
  return pc.length === 1; // C D E F G A B
}

function freqFromMidi(midi) {
  // A4 = 440Hz at MIDI 69
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/* ─────────────────────────────────────────────
   SAMPLE FILE CANDIDATES (MATCH YOUR FILES)
   ───────────────────────────────────────────── */

// ✅ Your public/audio uses:
// - Naturals: C.wav, C5.wav, A3.wav...
// - Sharps: A-sharp.wav, A-sharp3.wav, C-sharp5.wav...
// So we generate those stems FIRST, then optional fallbacks.
function filenameCandidates(noteRaw) {
  const s = String(noteRaw || "").trim();
  const m = s.match(/^([A-Ga-g])(#)?(\d+)?$/);
  if (!m) return [s];

  const L = m[1].toUpperCase();
  const isSharp = !!m[2];
  const oct = m[3] || ""; // optional digits

  if (!isSharp) {
    const list = [];
    if (oct) list.push(`${L}${oct}`); // A3
    list.push(`${L}`); // A
    return [...new Set(list)];
  }

  const list = [];
  if (oct) list.push(`${L}-sharp${oct}`); // A-sharp3
  list.push(`${L}-sharp`); // A-sharp

  // optional fallbacks (won't hurt)
  if (oct) list.push(`${L}sharp${oct}`);
  list.push(`${L}sharp`);

  // Avoid '#' stems as primary (Safari URL fragment weirdness + you don't name files that way)
  return [...new Set(list)];
}

/* ─────────────────────────────────────────────
   KEY GENERATION (RANGE-DRIVEN)
   ───────────────────────────────────────────── */

function buildKeysInRange(firstKey, lastKey) {
  const firstMidi = noteToMidi(firstKey);
  const lastMidi = noteToMidi(lastKey);

  if (firstMidi == null || lastMidi == null) {
    return buildKeysInRange("G3", "E5");
  }

  const lo = Math.min(firstMidi, lastMidi);
  const hi = Math.max(firstMidi, lastMidi);

  // White keys
  const whites = [];
  for (let m = lo; m <= hi; m++) {
    const { pc, octave } = midiToOctPc(m);
    if (!isNaturalPc(pc)) continue;
    const name = canonicalWhiteName(pc, octave);
    whites.push({ name, midi: m, letter: pc, octave });
  }

  // Black keys
  const blacks = [];
  for (let i = 0; i < whites.length; i++) {
    const w = whites[i];
    const canHaveSharp = ["C", "D", "F", "G", "A"].includes(w.letter);
    if (!canHaveSharp) continue;

    const blackMidi = w.midi + 1;
    if (blackMidi < lo || blackMidi > hi) continue;

    const { pc, octave } = midiToOctPc(blackMidi);
    if (!pc.endsWith("#")) continue;

    const letter = pc[0];
    const name = canonicalBlackName(letter, octave);

    blacks.push({ name, midi: blackMidi, after: w.name });
  }

  return { whites, blacks };
}

/* ─────────────────────────────────────────────
   AUDIO HELPERS (CACHE + REUSE)
   ───────────────────────────────────────────── */

// iPad/Safari-safe probe: load metadata (no fetch, no play)
function probeAudioUrl(url, timeoutMs = 1200) {
  return new Promise((resolve) => {
    try {
      const a = new Audio();
      let done = false;

      const finish = (ok) => {
        if (done) return;
        done = true;
        cleanup();
        resolve(ok);
      };

      const cleanup = () => {
        try {
          a.onloadedmetadata = null;
          a.oncanplaythrough = null;
          a.onerror = null;
        } catch {}
      };

      a.preload = "metadata";
      a.playsInline = true;

      a.onloadedmetadata = () => finish(true);
      a.oncanplaythrough = () => finish(true);
      a.onerror = () => finish(false);

      // Cache-bust only for the probe (prevents weird Safari cached 404s during dev)
      const bust = `__v=${Date.now()}`;
      const sep = url.includes("?") ? "&" : "?";
      a.src = `${url}${sep}${bust}`;

      setTimeout(() => finish(false), timeoutMs);
    } catch {
      resolve(false);
    }
  });
}

// Resolve URL once per note. Cache stores string URL OR null.
async function resolveSampleUrl(note, urlCacheRef, debug) {
  const cached = urlCacheRef.current.get(note);
  if (cached !== undefined) return cached; // string or null

  const stems = filenameCandidates(note);

  for (const base of AUDIO_BASES) {
    for (const stem of stems) {
      for (const ext of AUDIO_EXTS) {
        const url = `${base}/${encodeURIComponent(stem)}.${ext}`;

        // iPad-safe existence check
        const ok = await probeAudioUrl(url, 1200);

        if (debug) {
          // eslint-disable-next-line no-console
          console.log(`[Piano] probe ${ok ? "OK " : "NO "} ${url} for ${note}`);
        }

        if (ok) {
          urlCacheRef.current.set(note, url);
          if (debug) {
            // eslint-disable-next-line no-console
            console.log(`[Piano] Resolved sample URL: ${url} for ${note}`);
          }
          return url;
        }
      }
    }
  }

  urlCacheRef.current.set(note, null);
  if (debug) {
    // eslint-disable-next-line no-console
    console.warn(`[Piano] No sample found for ${note}.`);
  }
  return null;
}

async function playUrl(url, audioElCacheRef) {
  let a = audioElCacheRef.current.get(url);
  if (!a) {
    a = new Audio(url);
    a.preload = "auto";
    a.playsInline = true;
    a.crossOrigin = "anonymous";
    audioElCacheRef.current.set(url, a);
  }

  try {
    a.pause();
    a.currentTime = 0;
  } catch {}

  const p = a.play();
  if (p && typeof p.then === "function") await p;
  return true;
}

/* ─────────────────────────────────────────────
   COMPONENT
   ───────────────────────────────────────────── */

export default function VirtualPiano({
  targetMelody = [],
  onComplete = () => {},
  onNoteClick = () => {},
  onProgress = () => {}, // (index:number, total:number, wasCorrect:boolean)
  className = "",
  keyWidth = 44,
  whiteKeyHeight = 168,
  blackKeyWidth = 28,
  blackKeyHeight = 104,
  debug = false,

  // black-key label controls
  showAllBlackLabels = false,
  blackKeyLabels = {},

  // ✅ range controls (used by LevelTemplate)
  firstKey = "G3",
  lastKey = "E5",
}) {
  const containerRef = useRef(null);
  const audioCtxRef = useRef(null); // synth fallback only
  const urlCacheRef = useRef(new Map()); // note -> resolved URL (string) OR null
  const audioElCacheRef = useRef(new Map()); // url -> Audio element
  const [, setSeqIndex] = useState(0); // only setter used

  const isTablet = useMemo(() => isProbablyTablet(), []);

  // Debounce to swallow ghost/double taps on mobile
  const lastPressTsRef = useRef(0);
  const PRESS_DEBOUNCE_MS = 60;

  const targetSimple = useMemo(
    () =>
      (Array.isArray(targetMelody) ? targetMelody : [])
        .map(normalizeNoteName)
        .filter(Boolean),
    [targetMelody]
  );
  const total = targetSimple.length;

  const { whites, blacks } = useMemo(
    () => buildKeysInRange(firstKey, lastKey),
    [firstKey, lastKey]
  );

  // Init AudioContext on first gesture (for synth fallback only)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ensureCtx = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
      try {
        if (audioCtxRef.current?.state === "suspended") {
          audioCtxRef.current.resume().catch(() => {});
        }
      } catch {}
    };

    el.addEventListener("pointerdown", ensureCtx, { once: true });
    return () => el.removeEventListener("pointerdown", ensureCtx);
  }, []);

  // ✅ NEW: warmer electric-piano-ish fallback (ONLY when samples fail, and ONLY on tablets)
  const synthPluckElectric = useCallback((noteName) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const midi = noteToMidi(noteName);
    if (midi == null) return;

    const f = freqFromMidi(midi);
    const now = ctx.currentTime;

    // ---- EP tone knobs ----
    const ATTACK = 0.012; // softer than ping
    const DECAY = 0.24;
    const RELEASE = 0.42;
    const PEAK = 0.72;
    const SUSTAIN_LEVEL = 0.20;

    // low-pass to remove harsh highs
    const LP_CUTOFF = 2100; // lower = warmer/darker
    const LP_Q = 0.7;

    // warm mix (mostly triangle + tiny square "bite")
    const TRI_MIX = 0.92;
    const SQ_MIX = 0.08;

    // slight chorus/widening
    const DETUNE_CENTS = 9;

    // gentle tremolo
    const TREMOLO_HZ = 4.8;
    const TREMOLO_DEPTH = 0.055;

    // tone sources
    const tri1 = ctx.createOscillator();
    tri1.type = "triangle";
    tri1.frequency.setValueAtTime(f, now);
    tri1.detune.setValueAtTime(-DETUNE_CENTS, now);

    const tri2 = ctx.createOscillator();
    tri2.type = "triangle";
    tri2.frequency.setValueAtTime(f, now);
    tri2.detune.setValueAtTime(+DETUNE_CENTS, now);

    const sq = ctx.createOscillator();
    sq.type = "square";
    sq.frequency.setValueAtTime(f, now);

    const triGain1 = ctx.createGain();
    triGain1.gain.setValueAtTime(TRI_MIX * 0.5, now);
    const triGain2 = ctx.createGain();
    triGain2.gain.setValueAtTime(TRI_MIX * 0.5, now);

    const sqGain = ctx.createGain();
    sqGain.gain.setValueAtTime(SQ_MIX, now);

    tri1.connect(triGain1);
    tri2.connect(triGain2);
    sq.connect(sqGain);

    const mix = ctx.createGain();
    triGain1.connect(mix);
    triGain2.connect(mix);
    sqGain.connect(mix);

    // lowpass filter
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(LP_CUTOFF, now);
    lp.Q.setValueAtTime(LP_Q, now);

    // gentle saturation to warm it (waveshaper)
    const shaper = ctx.createWaveShaper();
    {
      const n = 2048;
      const curve = new Float32Array(n);
      const drive = 1.6; // 1.2 subtler, 2.0 stronger
      for (let i = 0; i < n; i++) {
        const x = (i * 2) / (n - 1) - 1;
        curve[i] = Math.tanh(drive * x);
      }
      shaper.curve = curve;
      shaper.oversample = "2x";
    }

    // amp envelope
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, now);
    amp.gain.linearRampToValueAtTime(PEAK, now + ATTACK);
    amp.gain.exponentialRampToValueAtTime(
      SUSTAIN_LEVEL,
      now + ATTACK + DECAY
    );
    amp.gain.exponentialRampToValueAtTime(
      0.0001,
      now + ATTACK + DECAY + RELEASE
    );

    // tremolo (LFO)
    const tremOsc = ctx.createOscillator();
    tremOsc.type = "sine";
    tremOsc.frequency.setValueAtTime(TREMOLO_HZ, now);

    const tremGain = ctx.createGain();
    tremGain.gain.setValueAtTime(TREMOLO_DEPTH, now);
    tremOsc.connect(tremGain);
    tremGain.connect(amp.gain);

    // chain
    mix.connect(lp).connect(shaper).connect(amp).connect(ctx.destination);

    tri1.start(now);
    tri2.start(now);
    sq.start(now);
    tremOsc.start(now);

    const stopAt = now + ATTACK + DECAY + RELEASE + 0.08;
    tri1.stop(stopAt);
    tri2.stop(stopAt);
    sq.stop(stopAt);
    tremOsc.stop(stopAt);
  }, []);

  // simple fallback (non-tablet) if you ever want it
  const synthPluckBasic = useCallback((noteName) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const midi = noteToMidi(noteName);
    if (midi == null) return;

    const f = freqFromMidi(midi);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = f;

    const now = ctx.currentTime;
    const attack = 0.005,
      decay = 0.18;

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.85, now + attack);
    gain.gain.exponentialRampToValueAtTime(0.0006, now + attack + decay);

    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + attack + decay + 0.05);
  }, []);

  const playNoteAudio = useCallback(
    async (note) => {
      const cachedUrl = urlCacheRef.current.get(note);

      // If already resolved, use it
      if (typeof cachedUrl === "string") {
        try {
          await playUrl(cachedUrl, audioElCacheRef);
          return true;
        } catch {
          urlCacheRef.current.delete(note);
        }
      }

      // If known missing, skip
      if (cachedUrl === null) return false;

      // Resolve (iPad-safe probe)
      const url = await resolveSampleUrl(note, urlCacheRef, debug);
      if (!url) return false;

      try {
        await playUrl(url, audioElCacheRef);
        return true;
      } catch {
        urlCacheRef.current.delete(note);
        return false;
      }
    },
    [debug]
  );

  const advanceProgress = useCallback(
    (note) => {
      if (!total) return;
      const simple = normalizeNoteName(note);

      setSeqIndex((i) => {
        let wasCorrect = false;
        let next = 0;

        if (simple === targetSimple[i]) {
          wasCorrect = true;
          next = i + 1;
        } else {
          next = simple === targetSimple[0] ? 1 : 0;
        }

        onProgress(Math.min(next, total), total, wasCorrect);

        if (next >= total) {
          onComplete();
          return 0;
        }
        return next;
      });
    },
    [onComplete, onProgress, targetSimple, total]
  );

  const handlePress = useCallback(
    (note, e) => {
      if (e) e.preventDefault();

      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now - lastPressTsRef.current < PRESS_DEBOUNCE_MS) return;
      lastPressTsRef.current = now;

      // UI/progress first
      advanceProgress(note);
      onNoteClick(note);

      // audio next
      playNoteAudio(note)
        .then((ok) => {
          if (ok) return;

          // ✅ fallback: EP synth ONLY on tablets/iPad
          if (isTablet) synthPluckElectric(note);
          else synthPluckBasic(note);

          if (debug) {
            // eslint-disable-next-line no-console
            console.log(`[Piano] Synth fallback used (${isTablet ? "EP" : "basic"}) for ${note}`);
          }
        })
        .catch(() => {
          if (isTablet) synthPluckElectric(note);
          else synthPluckBasic(note);
        });
    },
    [
      advanceProgress,
      onNoteClick,
      playNoteAudio,
      synthPluckElectric,
      synthPluckBasic,
      isTablet,
      debug,
    ]
  );

  const whiteIndexMap = useMemo(() => {
    const m = {};
    whites.forEach((w, i) => (m[w.name] = i));
    return m;
  }, [whites]);

  const widthPx = whites.length * keyWidth;

  return (
    <div
      ref={containerRef}
      className={`relative select-none ${className}`}
      style={{
        width: widthPx + "px",
        height: whiteKeyHeight + "px",
        touchAction: "manipulation",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* White keys */}
      {whites.map((w, i) => (
        <button
          key={`${w.name}-${i}`}
          type="button"
          data-note={w.name}
          onPointerDown={(e) => handlePress(w.name, e)}
          className="absolute top-0 border border-gray-300 bg-white active:bg-gray-100 shadow-sm rounded-b-md"
          style={{
            left: i * keyWidth,
            width: keyWidth - 1,
            height: whiteKeyHeight,
            zIndex: 1,
            touchAction: "manipulation",
          }}
          aria-label={w.name}
          title={w.name}
        >
          <span className="absolute bottom-1 left-1 text-[11px] text-gray-600">
            {w.name}
          </span>
        </button>
      ))}

      {/* Black keys */}
      {blacks.map((b, i) => {
        const idx = whiteIndexMap[b.after];
        if (idx == null) return null;

        const left = idx * keyWidth + Math.floor(0.72 * keyWidth);
        const labelOverride = blackKeyLabels[b.name];
        const showLabel = showAllBlackLabels || !!labelOverride;

        return (
          <button
            key={`${b.name}-${i}`}
            type="button"
            data-note={b.name}
            onPointerDown={(e) => handlePress(b.name, e)}
            className="absolute top-0 bg-black active:bg-gray-800 rounded-b-md"
            style={{
              left,
              width: blackKeyWidth,
              height: blackKeyHeight,
              zIndex: 2,
              border: "1px solid #111",
              boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
              touchAction: "manipulation",
            }}
            aria-label={b.name}
            title={b.name}
          >
            {showLabel ? (
              <span className="absolute bottom-1 left-1 text-[11px] text-gray-100">
                {labelOverride || normalizeNoteName(b.name)}
              </span>
            ) : (
              <span className="sr-only">{b.name}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}